import {
    isPhoneNumberE164,
    ReferenceBusinessId,
    TaskTypeId,
    toPhoneNumberE164,
} from "@sp-crm/core";
import { EntityContactDetails } from "components/details-page-v-next/entity-contact-details";
import { tierColorForToken } from "components/references/show-reference/tier-colors";
import { VisitParent } from "components/visits/types";
import { AdvancedSearchEntityType } from "generated/graphql";
import React from "react";
import { Link } from "react-router-dom";
import { orgPath } from "./agenda-actions";
import { AgendaRecentActivity } from "./agenda-recent-activity";
import { agendaOrgDetails, AgendaOrgGroup } from "./marketing-plan-agenda";
import { OrgCallOptions, TouchpointRow } from "./touchpoint-row";

interface AgendaOrgCardProps {
    group: AgendaOrgGroup;
    hasPhoneLine: boolean;
    onSendEmail: (
        businessId: ReferenceBusinessId,
        activityTypeId: TaskTypeId | null,
    ) => void;
}

export const AgendaOrgCard: React.FC<AgendaOrgCardProps> = ({
    group,
    hasPhoneLine,
    onSendEmail,
}) => {
    const color = tierColorForToken(group.tierColor);
    const phone =
        group.businessPhone && isPhoneNumberE164(group.businessPhone)
            ? toPhoneNumberE164(group.businessPhone)
            : null;
    const businessId = group.businessId as ReferenceBusinessId;
    const parent: VisitParent = {
        kind: "organization",
        id: businessId,
        name: group.businessName,
        address: null,
    };
    const orgCallOptions: OrgCallOptions = {
        businessId,
        orgPhone: phone,
        orgName: group.businessName,
        callableContacts: group.callableContacts,
    };

    return (
        <div className="rounded-lg bg-white p-4 ring-1 ring-gray-300 sm:p-5">
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <Link
                        to={orgPath(group.businessId)}
                        className="block truncate font-semibold text-gray-900 hover:underline">
                        {group.businessName}
                    </Link>
                </div>
                {group.tierName ? (
                    <span
                        className={`inline-flex flex-shrink-0 items-center rounded-full ${color.bg} ${color.text} px-2.5 py-0.5 text-xs font-medium`}>
                        {group.tierName}
                    </span>
                ) : null}
            </div>
            <div className="mt-2">
                <EntityContactDetails
                    entityId={businessId}
                    entityType={AdvancedSearchEntityType.ReferenceBusiness}
                    details={agendaOrgDetails(group)}
                />
            </div>
            <div className="mt-3 divide-y divide-gray-100 border-t border-gray-100">
                {group.touchpoints.map(touchpoint => (
                    <TouchpointRow
                        key={touchpoint.touchpointId}
                        touchpoint={touchpoint}
                        parent={parent}
                        email={group.businessEmail}
                        emailOptedOut={false}
                        phone={phone}
                        hasPhoneLine={hasPhoneLine}
                        orgCallOptions={orgCallOptions}
                        onSendEmail={activityTypeId =>
                            onSendEmail(businessId, activityTypeId)
                        }
                    />
                ))}
            </div>
            <AgendaRecentActivity items={group.recentActivity} />
        </div>
    );
};
